import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Bot, Clock, Loader2, MessageSquare, Pause, Play, User, X } from 'lucide-react'
import { callsService } from '../../../services/callsService'
import { ordersService } from '../../../services/ordersService'
import { STATUS_LABEL, STATUS_STYLE } from '../../../constants/orderStatus'
import { formatters } from '../../../utils/formatters'
import type { Call, CallMessage, Order } from '../../../types/api.types'

interface CallDetailDrawerProps {
  call: Call | null
  // Orders pushed by the stream. A match here is fresher than what we fetched.
  liveOrders: Map<number, Order>
  onClose: () => void
}

// Clicking a line means "play from here", so the highlight would otherwise
// yank the list out from under the click.
const SCROLL_SUPPRESS_MS = 1000

function turnStyle(role: CallMessage['role'], active: boolean): string {
  const base =
    role === 'customer'
      ? 'border-blue-400 bg-blue-50 hover:bg-blue-100'
      : 'border-brand-green bg-gray-50 hover:bg-gray-100'
  return active ? `${base} ring-2 ring-brand-green/50` : base
}

export function CallDetailDrawer({
  call,
  liveOrders,
  onClose,
}: CallDetailDrawerProps): JSX.Element {
  const isOpen = call !== null

  const [isPlaying, setIsPlaying] = useState(false)
  const [loadingAudio, setLoadingAudio] = useState(false)
  const [audioError, setAudioError] = useState<string | null>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  const [turns, setTurns] = useState<CallMessage[]>([])
  const [turnsLoading, setTurnsLoading] = useState(false)
  const [order, setOrder] = useState<Order | null>(null)

  // Pulled out so the effects below depend on the ids they actually read,
  // rather than on the whole call object.
  const callId = call?.id ?? null
  const orderId = call?.order_id ?? null

  const audioRef = useRef<HTMLAudioElement>(null)
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([])
  // Which call the current <audio> src belongs to. The link is minted per call
  // and expires, so "do we have one" is per-call, not a boolean.
  const loadedForRef = useRef<number | null>(null)
  const remintedRef = useRef(false)
  const pendingSeekRef = useRef<number | null>(null)
  const suppressScrollUntilRef = useRef(0)

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [isOpen, onClose])

  // Tear the source down rather than swapping it — a stale SAS link left on the
  // element will 403 the moment anything touches it.
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.removeAttribute('src')
      audioRef.current.load()
    }
    loadedForRef.current = null
    remintedRef.current = false
    pendingSeekRef.current = null
    setIsPlaying(false)
    setLoadingAudio(false)
    setAudioError(null)
    setCurrentTime(0)
    setDuration(0)
    rowRefs.current = []
  }, [callId])

  const mintSource = useCallback(async (): Promise<boolean> => {
    const audio = audioRef.current
    if (!audio || !call) return false
    try {
      const { url } = await callsService.audioUrl(call.id)
      audio.src = url
      loadedForRef.current = call.id
      setAudioError(null)
      return true
    } catch {
      setAudioError('Could not load the recording.')
      return false
    }
  }, [call])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const onLoadedMetadata = (): void => {
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0)
      if (pendingSeekRef.current != null) {
        audio.currentTime = pendingSeekRef.current
        pendingSeekRef.current = null
      }
    }
    const onTimeUpdate = (): void => setCurrentTime(audio.currentTime)
    const onEnded = (): void => {
      setIsPlaying(false)
      setCurrentTime(0)
    }
    // The signed link lasts an hour and a drawer left open outlives it. Re-mint
    // once and resume where we were; a second failure is a real fault.
    const onError = (): void => {
      setIsPlaying(false)
      if (loadedForRef.current == null || remintedRef.current) {
        if (loadedForRef.current != null) setAudioError('Could not load the recording.')
        return
      }
      remintedRef.current = true
      pendingSeekRef.current = audio.currentTime || null
      void mintSource().then((ok) => {
        if (ok) void audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false))
      })
    }

    audio.addEventListener('loadedmetadata', onLoadedMetadata)
    audio.addEventListener('timeupdate', onTimeUpdate)
    audio.addEventListener('ended', onEnded)
    audio.addEventListener('error', onError)
    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata)
      audio.removeEventListener('timeupdate', onTimeUpdate)
      audio.removeEventListener('ended', onEnded)
      audio.removeEventListener('error', onError)
    }
  }, [mintSource])

  useEffect(() => {
    if (callId == null) {
      setTurns([])
      return
    }
    let cancelled = false
    setTurnsLoading(true)
    callsService
      .messages(callId)
      .then((rows) => {
        if (!cancelled) setTurns(rows)
      })
      .catch(() => {
        if (!cancelled) setTurns([])
      })
      .finally(() => {
        if (!cancelled) setTurnsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [callId])

  useEffect(() => {
    if (orderId == null) {
      setOrder(null)
      return
    }
    let cancelled = false
    ordersService
      .getById(orderId)
      .then((row) => {
        if (!cancelled) setOrder(row)
      })
      .catch(() => {
        if (!cancelled) setOrder(null)
      })
    return () => {
      cancelled = true
    }
  }, [orderId])

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current
    if (!audio || !call) return
    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
      return
    }
    if (loadedForRef.current !== call.id) {
      setLoadingAudio(true)
      const ok = await mintSource()
      setLoadingAudio(false)
      if (!ok) return
    }
    try {
      await audio.play()
      setIsPlaying(true)
    } catch {
      setIsPlaying(false)
    }
  }, [isPlaying, call, mintSource])

  const seekToMs = useCallback(
    async (ms: number) => {
      const audio = audioRef.current
      if (!audio || !call) return
      const seconds = ms / 1000
      suppressScrollUntilRef.current = Date.now() + SCROLL_SUPPRESS_MS
      setCurrentTime(seconds)

      if (loadedForRef.current !== call.id) {
        pendingSeekRef.current = seconds
        setLoadingAudio(true)
        const ok = await mintSource()
        setLoadingAudio(false)
        if (!ok) return
      } else {
        audio.currentTime = seconds
      }
      try {
        await audio.play()
        setIsPlaying(true)
      } catch {
        setIsPlaying(false)
      }
    },
    [call, mintSource],
  )

  const handleScrub = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current
    if (!audio) return
    const time = parseFloat(e.target.value)
    suppressScrollUntilRef.current = Date.now() + SCROLL_SUPPRESS_MS
    if (loadedForRef.current != null) audio.currentTime = time
    else pendingSeekRef.current = time
    setCurrentTime(time)
  }, [])

  const activeIndex = useMemo(
    () =>
      turns.findIndex(
        (turn) =>
          currentTime * 1000 >= turn.start_offset_ms && currentTime * 1000 < turn.end_offset_ms,
      ),
    [turns, currentTime],
  )

  useEffect(() => {
    if (activeIndex < 0) return
    if (Date.now() < suppressScrollUntilRef.current) return
    rowRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  // audio_duration_ms is read server-side from the WAV, so it is trustworthy
  // for the scrubber before the file itself has loaded.
  const trackDuration = duration || (call?.audio_duration_ms ?? 0) / 1000
  const liveOrder = orderId != null ? liveOrders.get(orderId) : undefined
  const shownOrder = liveOrder ?? order

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} aria-hidden="true" />
      )}
      <div
        className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l border-[#E5E7EB] bg-white transition-transform duration-300 ease-in-out"
        style={{ transform: isOpen ? 'translateX(0)' : 'translateX(100%)', willChange: 'transform' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="call-drawer-title"
      >
        <audio ref={audioRef} preload="none" />

        <div className="flex items-center justify-between border-b border-[#E5E7EB] p-5">
          <div className="min-w-0">
            <h2 id="call-drawer-title" className="text-[18px] font-bold text-gray-900">
              Table {call?.table_number ?? '—'}
            </h2>
            {call && (
              <p className="text-[12px] text-gray-400">
                Call #{call.id} · {formatters.formatDate(call.started_at)}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close conversation"
            type="button"
          >
            <X size={20} />
          </button>
        </div>

        {call && (
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
            <div className="rounded-xl border border-[#E5E7EB] bg-white p-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={togglePlay}
                  disabled={loadingAudio}
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-green transition-opacity hover:opacity-90 disabled:opacity-50"
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                  type="button"
                >
                  {loadingAudio ? (
                    <Loader2 size={18} className="animate-spin text-black" />
                  ) : isPlaying ? (
                    <Pause size={18} className="text-black" />
                  ) : (
                    <Play size={18} className="ml-0.5 text-black" />
                  )}
                </button>
                <div className="min-w-0 flex-1">
                  <input
                    type="range"
                    min={0}
                    max={trackDuration || 0}
                    step={0.1}
                    value={currentTime}
                    onChange={handleScrub}
                    aria-label="Seek"
                    className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-gray-200 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand-green"
                  />
                  <div className="mt-1 flex justify-between text-[11px] text-gray-400">
                    <span>{formatters.formatDuration(currentTime)}</span>
                    <span>{formatters.formatDuration(trackDuration || null)}</span>
                  </div>
                </div>
              </div>
              {audioError && <p className="mt-3 text-[12px] text-red-600">{audioError}</p>}
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2 text-[13px] text-gray-500">
                <Clock size={14} />
                <span>
                  {turns.length} {turns.length === 1 ? 'turn' : 'turns'} · tap a line to jump there
                </span>
              </div>

              {turnsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={20} className="animate-spin text-brand-green" />
                </div>
              ) : turns.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[#E5E7EB] p-6 text-center">
                  <MessageSquare size={24} className="mx-auto mb-2 text-gray-300" />
                  <p className="text-[13px] text-gray-500">
                    Nobody spoke on this call. The recording is still the record of that.
                  </p>
                </div>
              ) : (
                <ul className="space-y-2">
                  {turns.map((turn, i) => (
                    <li key={turn.turn_index}>
                      <button
                        ref={(el) => {
                          rowRefs.current[i] = el
                        }}
                        type="button"
                        onClick={() => void seekToMs(turn.start_offset_ms)}
                        aria-current={i === activeIndex}
                        className={`flex w-full gap-3 rounded-lg border-l-4 p-3 text-left transition-colors ${turnStyle(turn.role, i === activeIndex)}`}
                      >
                        <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-white">
                          {turn.role === 'customer' ? (
                            <User size={13} className="text-blue-600" />
                          ) : (
                            <Bot size={13} className="text-gray-600" />
                          )}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="mb-0.5 flex items-center gap-2">
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                              {turn.role}
                            </span>
                            <span className="text-[11px] tabular-nums text-gray-400">
                              {formatters.formatDuration(turn.start_offset_ms / 1000)}
                            </span>
                          </span>
                          <span className="block text-[14px] leading-relaxed text-gray-800">
                            {turn.text}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-xl border border-[#E5E7EB] p-4">
              {call.order_id == null ? (
                <p className="text-[13px] text-gray-500">
                  This conversation did not end in an order.
                </p>
              ) : shownOrder == null ? (
                <p className="text-[13px] text-gray-400">Loading order #{call.order_id}...</p>
              ) : (
                <>
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <h3 className="text-[14px] font-semibold text-gray-900">
                      Order #{shownOrder.id}
                    </h3>
                    <span
                      className={`shrink-0 rounded px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLE[shownOrder.status]}`}
                    >
                      {STATUS_LABEL[shownOrder.status]}
                    </span>
                  </div>
                  <ul className="space-y-1.5 text-[13px]">
                    {shownOrder.items.map((item, i) => (
                      <li key={`${shownOrder.id}-${i}`} className="flex gap-2">
                        <span className="shrink-0 font-medium text-gray-500">{item.quantity}×</span>
                        <span className="min-w-0 flex-1 text-gray-800">{item.name}</span>
                        <span className="shrink-0 tabular-nums text-gray-500">
                          {item.line_total}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {shownOrder.notes && (
                    <p className="mt-2 text-[12px] italic text-amber-700">{shownOrder.notes}</p>
                  )}
                  <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-2 text-[13px]">
                    <span className="text-gray-400">Total</span>
                    <span className="font-semibold text-gray-900">
                      {shownOrder.currency} {shownOrder.total_amount}
                    </span>
                  </div>
                  {/* ponytail: no status buttons here — the Orders board owns the
                      409 transition rules, and a second copy would drift. */}
                  <a
                    href="/orders"
                    className="mt-3 inline-block text-[13px] font-medium text-brand-green hover:underline"
                  >
                    View in Orders
                  </a>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
